import * as React from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  Select,
  MenuItem
} from "../../../components/mui";
import { AddRoundedIcon } from "../../../components/icons";
import Modal from "../../../components/shared/Modal";
import TablePagination from "../../../components/shared/TablePagination";
import CondoUnitInfoCard from "../components/CondoUnitInfoCard";
import ConfirmDialog from "../../../components/shared/ConfirmDialog";
import UtilityFormModal from "./UtilityFormModal";
import { useToast } from "../../../components/shared/ToastProvider";

import "../../../styles/main.css";
import "../../../styles/pages/condoUnitDetail.css";
import "../../../styles/components/dataTableCard.css";

/**
 * ManageUtilitiesModal
 * Same layout as ManageMiscExpenseModal but for utilities table.
 * UI-only; backend will wire data/actions later.
 */
export default function ManageUtilitiesModal({
  open,
  onClose,
  condoItems = [],
  rows = []
}) {
  const { showToast } = useToast();
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [formMode, setFormMode] = React.useState("add"); // "add" | "edit"
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [rowToDelete, setRowToDelete] = React.useState(null);

  const totalEntries = rows.length;
  const totalPages = Math.max(1, Math.ceil(Math.max(totalEntries, 1) / rowsPerPage));

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    setRowsPerPage(value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const startIndex = (page - 1) * rowsPerPage;
  const paginatedRows = rows.slice(startIndex, startIndex + rowsPerPage);
  const startEntry = totalEntries === 0 ? 0 : startIndex + 1;
  const endEntry = totalEntries === 0 ? 0 : Math.min(totalEntries, startIndex + paginatedRows.length);

  const handleAddClick = () => {
    setFormMode("add");
    setSelectedRow(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (row) => {
    setFormMode("edit");
    setSelectedRow(row);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    // UI-only: parent will handle actual delete logic later.
    setIsDeleteOpen(false);
    setRowToDelete(null);
    showToast({ message: "Utility record deleted", severity: "success" });
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
    </>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Manage Utilities"
        actions={actions}
        maxWidth="lg"
        fullWidth
      >
        <Box className="modal-form-grid" style={{ marginTop: 8 }}>
          {/* Condo Unit Info (display only) */}
          <Box mb={2} className="modal-condo-unit-info-card">
            <CondoUnitInfoCard
              title="Condo Unit Info"
              items={condoItems}
              showEdit={false}
            />
          </Box>

          {/* Utilities table */}
          <Box mb={1} display="flex" justifyContent="flex-start">
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={handleAddClick}
            >
              Add
            </Button>
          </Box>

          {/* Entries control (Show X entries) */}
          <Box className="data-table-controls" mb={1}>
            <Box className="data-table-control">
              <Typography component="span" variant="body2" className="data-table-control-label">
                Show
              </Typography>
              <FormControl size="small" className="data-table-select">
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  {[10, 20, 30].map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography component="span" variant="body2" className="data-table-control-label">
                entries
              </Typography>
            </Box>
          </Box>

          <TableContainer className="condo-unit-detail-table-container">
            <Table size="small" className="condo-unit-detail-table">
              <TableHead className="condo-unit-detail-table-head">
                <TableRow>
                  <TableCell className="modal-table-actions-cell" />
                  <TableCell>Type</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Account No</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Payable To</TableCell>
                  <TableCell>In-Charge</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => (
                    <TableRow key={`utilities-modal-row-${startIndex + index}`}>
                      <TableCell className="modal-table-actions-cell">
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleEditClick(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(row)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.accountName}</TableCell>
                      <TableCell>{row.accountNo}</TableCell>
                      <TableCell className="condo-unit-detail-amount-cell">{row.amount}</TableCell>
                      <TableCell>{row.startDate}</TableCell>
                      <TableCell>{row.payableTo || ""}</TableCell>
                      <TableCell>{row.inCharge || ""}</TableCell>
                      <TableCell>{row.remarks || ""}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      className="condo-unit-detail-empty-cell"
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination summary and controls */}
          <Box className="data-table-footer" mt={1}>
            <Typography variant="caption" className="data-table-summary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <TablePagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Box>
        </Box>
      </Modal>

      <ConfirmDialog
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Record"
        message="Are you sure you want to delete this record? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      <UtilityFormModal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        mode={formMode}
        initialValues={selectedRow || {}}
      />
    </>
  );
}
